import { ConfigProvider, Table, Typography } from "antd";
import type { ColumnsType, TableRowSelection } from "antd/es/table/interface";
import React, { useMemo } from "react";
import { formatCurrency } from "../../../../libs/utils";
import { AddInvoicesButton } from "./AddInvoicesButton";
import { AllocationRowCells } from "./AllocationRow";
import { Invoice } from "../types";
import { SummarySection } from "./SummarySection";
import { ReceiptInvoiceAllocationRow } from "./types";
import styles from "./ReceiptMultiInvoiceAllocation.module.scss";
import { dateByformat } from "../../../../../utils/dateHelper";

const { Text } = Typography;

type InvoiceAllocationTableProps = {
  invoices: ReceiptInvoiceAllocationRow[];
  selectedCount: number;
  totalAllocated: number;
  allocationLimit: number;
  allocationExceedsLimit: boolean;
  hasSelectedInvoices: boolean;
  overpaymentCredit: number;
  applyOverpaymentCredit?: boolean;
  disabled?: boolean;
  leaseId?: string;
  tenantId?: string;
  pendingInvoicesSubtitle?: string;
  onSelectionChange: (selectedRowKeys: React.Key[]) => void;
  onAllocationChange: (id: string, value: number) => void;
  onAllocationInputValidityChange?: (id: string, isValid: boolean) => void;
  onRemove: (id: string) => void;
  onAddInvoice: (invoice: Invoice) => void;
  onInvoiceClick?: (invoice: ReceiptInvoiceAllocationRow) => void;
};

export const InvoiceAllocationTable: React.FC<InvoiceAllocationTableProps> = ({
  invoices,
  selectedCount,
  totalAllocated,
  allocationLimit,
  allocationExceedsLimit,
  hasSelectedInvoices,
  overpaymentCredit,
  applyOverpaymentCredit = false,
  disabled,
  leaseId,
  tenantId,
  pendingInvoicesSubtitle,
  onSelectionChange,
  onAllocationChange,
  onAllocationInputValidityChange,
  onRemove,
  onAddInvoice,
  onInvoiceClick,
}) => {
  const allocationExceededMessage = applyOverpaymentCredit
    ? `Total applied exceeds available credit of ${formatCurrency(allocationLimit, { symbol: true })}. Please reduce allocations to continue.`
    : `Total allocation exceeds receipt amount of ${formatCurrency(allocationLimit, { symbol: true })}. Please reduce allocations to continue.`;

  const addedInvoiceIds = useMemo(() => invoices.map((invoice) => invoice.id), [invoices]);
  const selectedRowKeys = useMemo(
    () => invoices.filter((invoice) => invoice.selected).map((invoice) => invoice.id),
    [invoices],
  );

  const rowSelection: TableRowSelection<ReceiptInvoiceAllocationRow> = {
    selectedRowKeys,
    onChange: onSelectionChange,
    columnWidth: 48,
    getCheckboxProps: (record) => ({
      disabled: disabled,
    }),
  };
  
  const columns: ColumnsType<ReceiptInvoiceAllocationRow> = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      ellipsis: true,
      render: (_, record) => <AllocationRowCells.InvoiceNo record={record} onInvoiceClick={onInvoiceClick} />,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      ellipsis: true,
      render: (_, record) => <AllocationRowCells.Type record={record} />,
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      width: 96,
      render: (_, record) => (
        <Text className={styles.cellText}>{record.dueDate ? dateByformat(record.dueDate, "DD-MM-YYYY") : "--"}</Text>
      ),
    },
    {
      title: "Invoice Amount",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
      align: "right",
      width: 112,
      className: styles.colNumeric,
      onHeaderCell: () => ({ className: styles.colNumericHeader }),
      render: (_, record) => (
        <span className={styles.numericCell}>{formatCurrency(record.invoiceAmount, { symbol: true })}</span>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      // width: 88,
      className: styles.colNumeric,
      onHeaderCell: () => ({ className: styles.colNumericHeader }),
      render: (_, record) => <AllocationRowCells.Balance record={record} />,
    },
    {
      title: "Allocation (AED)",
      dataIndex: "allocation",
      key: "allocation",
      align: "right",
      width: 128,
      className: styles.colAllocation,
      onHeaderCell: () => ({ className: styles.colNumericHeader }),
      render: (_, record) => (
        <AllocationRowCells.Allocation
          record={record}
          disabled={disabled}
          onAllocationChange={onAllocationChange}
          onAllocationInputValidityChange={onAllocationInputValidityChange}
          onRemove={onRemove}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 36,
      align: "center",
      className: styles.colActions,
      render: (_, record) => (
        <AllocationRowCells.Actions
          record={record}
          disabled={disabled}
          onAllocationChange={onAllocationChange}
          onRemove={onRemove}
        />
      ),
    },
  ];

  return (
    <div className={styles.invoicesBlock}>
      <div className={styles.invoicesSectionHeader}>
        <div>
          <Text className={styles.invoicesTitle}>Invoices payable by this receipt</Text>
          <div className={styles.invoicesSelectionMeta}>
            <Text className={styles.invoicesSubtitle}>{selectedCount} selected</Text>
            {allocationExceedsLimit && (
              <Text className={`ag-error ${styles.allocationExceededError}`}>{allocationExceededMessage}</Text>
            )}
          </div>
        </div>
        <AddInvoicesButton
          leaseId={leaseId}
          tenantId={tenantId}
          subtitle={pendingInvoicesSubtitle}
          disabled={disabled}
          addedInvoiceIds={addedInvoiceIds}
          onAddInvoice={onAddInvoice}
        />
      </div>

      <div className={styles.tableContainer}>
        <ConfigProvider
          theme={{
            components: {
              Checkbox: {
                borderRadius: 0,
                borderRadiusSM: 0,
                borderRadiusLG: 0,
              },
            },
          }}
        >
          <Table<ReceiptInvoiceAllocationRow>
            className={styles.allocationTable}
            columns={columns}
            dataSource={invoices}
            rowKey="id"
            pagination={false}
            rowSelection={rowSelection}
            size="middle"
            tableLayout="fixed"
            locale={{ emptyText: "No invoices selected." }}
            summary={() =>
              !applyOverpaymentCredit && hasSelectedInvoices && overpaymentCredit > 0 ? (
                <Table.Summary>
                  <Table.Summary.Row className={styles.overpaymentSummaryRow}>
                    <Table.Summary.Cell index={0} colSpan={6} className={styles.overpaymentSummaryLabelCell}>
                      <span className={styles.overpaymentTitle}>Overpayment Credit</span>
                      <span className={styles.overpaymentSubtext}>Saved to tenant credit balance.</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right" className={styles.overpaymentSummaryAmountCell}>
                      <span className={styles.overpaymentAmount}>
                        {formatCurrency(overpaymentCredit, { symbol: true })}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} className={styles.overpaymentSummaryActionCell} />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        </ConfigProvider>

        <SummarySection
          totalAllocated={totalAllocated}
          overpaymentCredit={overpaymentCredit}
          hasSelectedInvoices={hasSelectedInvoices}
          applyOverpaymentCredit={applyOverpaymentCredit}
        />
      </div>
    </div>
  );
};
